import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  BarChart3, Users, Trophy, Gamepad2, Calendar, 
  TrendingUp, Award, Loader2, Crown, Target
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Statistics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [activeTab, setActiveTab] = useState("platform");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const platformRes = await api.getPlatformStatistics();
        setPlatformStats(platformRes.data);
        
        if (user) {
          const userRes = await api.getUserStatistics(user.user_id);
          setUserStats(userRes.data);
        }
      } catch (error) {
        console.error("Error fetching statistics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="statistics-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          İstatistikler
        </h1>
        <p className="text-[#B1C1C1] mt-1">Platform ve kişisel istatistikler</p>
      </div>

      {/* Tab Buttons */}
      {user && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("platform")}
            className={`px-4 py-2 rounded-lg font-['Barlow_Condensed'] uppercase tracking-wider text-sm transition-all ${
              activeTab === "platform"
                ? "bg-[#4DFFFF] text-[#050E2E] font-bold"
                : "bg-white/5 text-[#B1C1C1] hover:bg-white/10"
            }`}
            data-testid="tab-platform"
          >
            Platform
          </button>
          <button
            onClick={() => setActiveTab("personal")}
            className={`px-4 py-2 rounded-lg font-['Barlow_Condensed'] uppercase tracking-wider text-sm transition-all ${
              activeTab === "personal"
                ? "bg-[#4DFFFF] text-[#050E2E] font-bold"
                : "bg-white/5 text-[#B1C1C1] hover:bg-white/10"
            }`}
            data-testid="tab-personal"
          >
            Kişisel
          </button>
        </div>
      )}

      {activeTab === "platform" && platformStats && (
        <div className="space-y-6 animate-fade-in-up">
          {/* Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="glass-card p-4 text-center">
              <Users className="w-8 h-8 text-[#4DFFFF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{platformStats.overview.total_users}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Kullanıcı</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Trophy className="w-8 h-8 text-[#C6A87C] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{platformStats.overview.total_leagues}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Lig</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Target className="w-8 h-8 text-[#F61225] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{platformStats.overview.total_tournaments}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Turnuva</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Calendar className="w-8 h-8 text-[#9333EA] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{platformStats.overview.total_games_played}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Oynanan Oyun</p>
            </div>
            <div className="glass-card p-4 text-center">
              <TrendingUp className="w-8 h-8 text-[#059669] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{platformStats.overview.recent_activities}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Bu Hafta</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Game Popularity */}
            <div className="glass-card overflow-hidden">
              <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5 text-[#C6A87C]" />
                  <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                    Oyun Popülerliği
                  </h2>
                </div>
              </div>
              
              <div className="p-4 space-y-3">
                {platformStats.game_popularity.map((game, index) => (
                  <div key={game.game_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? "bg-[#C6A87C] text-[#050E2E]" :
                      index === 1 ? "bg-gray-400 text-[#050E2E]" :
                      index === 2 ? "bg-[#CD7F32] text-[#050E2E]" :
                      "bg-white/10 text-white"
                    }`}>
                      {index + 1}
                    </span>
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${game.color}33` }}
                    >
                      <Gamepad2 className="w-4 h-4" style={{ color: game.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{game.name}</p>
                      <div className="flex items-center gap-2 text-xs text-[#64748B]">
                        <span>{game.league_count} lig</span>
                        <span>•</span>
                        <span>{game.tournament_count} turnuva</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#4DFFFF]">{game.total}</p>
                      <p className="text-xs text-[#64748B]">toplam</p>
                    </div>
                  </div>
                ))}
                
                {platformStats.game_popularity.length === 0 && (
                  <p className="text-center text-[#64748B] py-4">Henüz veri yok</p>
                )}
              </div>
            </div>

            {/* Top Users */}
            <div className="glass-card overflow-hidden">
              <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#C6A87C]" />
                  <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                    En Aktif Kullanıcılar
                  </h2>
                </div>
              </div>
              
              <div className="p-4 space-y-3">
                {platformStats.top_users.map((topUser, index) => (
                  <Link 
                    key={topUser.user_id}
                    to={`/profile/${topUser.username}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? "bg-[#C6A87C] text-[#050E2E]" :
                      index === 1 ? "bg-gray-400 text-[#050E2E]" :
                      index === 2 ? "bg-[#CD7F32] text-[#050E2E]" :
                      "bg-white/10 text-white"
                    }`}>
                      {index + 1}
                    </span>
                    <div className="w-10 h-10 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                      {topUser.picture ? (
                        <img src={topUser.picture} alt={topUser.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold text-[#4DFFFF]">
                          {topUser.name?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{topUser.name}</p>
                      <p className="text-xs text-[#64748B]">@{topUser.username}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#4DFFFF]">{topUser.leagues_count}</p>
                      <p className="text-xs text-[#64748B]">lig</p>
                    </div>
                  </Link>
                ))}
                
                {platformStats.top_users.length === 0 && (
                  <p className="text-center text-[#64748B] py-4">Henüz veri yok</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "personal" && userStats && (
        <div className="space-y-6 animate-fade-in-up">
          {/* Personal Summary */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="glass-card p-4 text-center">
              <Trophy className="w-8 h-8 text-[#C6A87C] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{userStats.summary.total_leagues}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Lig</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Target className="w-8 h-8 text-[#F61225] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{userStats.summary.total_tournaments}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Turnuva</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Calendar className="w-8 h-8 text-[#4DFFFF] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{userStats.summary.total_games_played}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Oyun</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Award className="w-8 h-8 text-[#9333EA] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{userStats.summary.total_wins}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">1. Sıra</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Crown className="w-8 h-8 text-[#059669] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{userStats.summary.tournament_wins}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Turnuva Şamp.</p>
            </div>
            <div className="glass-card p-4 text-center">
              <TrendingUp className="w-8 h-8 text-[#F308C9] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">%{userStats.summary.win_rate}</p>
              <p className="text-xs text-[#64748B] uppercase tracking-wider">Kazanma</p>
            </div>
          </div>

          {/* Game-by-Game Stats */}
          <div className="glass-card overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A1635] to-[#0F1F45] px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#4DFFFF]" />
                <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                  Oyun Bazlı Performans
                </h2>
              </div>
            </div>
            
            {userStats.game_stats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="league-table">
                  <thead>
                    <tr>
                      <th className="text-left">Oyun</th>
                      <th className="text-center">Lig</th>
                      <th className="text-center">Oyun</th>
                      <th className="text-center">🥇</th>
                      <th className="text-center">🥈</th>
                      <th className="text-center">🥉</th>
                      <th className="text-center">Puan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userStats.game_stats.map((stat) => (
                      <tr key={stat.game_id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <Gamepad2 className="w-4 h-4 text-[#C6A87C]" />
                            <span className="text-white">{stat.game_name}</span>
                          </div>
                        </td>
                        <td className="text-center text-[#B1C1C1]">{stat.leagues_count}</td>
                        <td className="text-center text-[#B1C1C1]">{stat.weeks_played}</td>
                        <td className="text-center text-[#C6A87C] font-bold">{stat.first_places}</td>
                        <td className="text-center text-gray-400">{stat.second_places}</td>
                        <td className="text-center text-[#CD7F32]">{stat.third_places}</td>
                        <td className="text-center">
                          <span className="text-lg font-bold text-[#4DFFFF]">{stat.total_points}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <BarChart3 className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#B1C1C1]">Henüz oyun istatistiği yok</p>
                <p className="text-sm text-[#64748B] mt-1">Bir lige katıl ve oyun oyna</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!user && activeTab === "personal" && (
        <div className="glass-card p-12 text-center">
          <Users className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <p className="text-[#B1C1C1] text-lg">Kişisel istatistikler için giriş yapın</p>
          <Link to="/login" className="text-[#4DFFFF] hover:underline mt-2 inline-block">
            Giriş Yap
          </Link>
        </div>
      )}
    </div>
  );
}
